import { WritableAtom, atom } from 'jotai';
import { createStore } from 'jotai/index';
import { nanoid } from 'nanoid';
import { mapEStreamTradeToInstrumentStoreItem } from '../../services/estream-mapper/eStreamTradeMapper';
import { ObjectSet } from '../../utils';
import { hasIntersection, uniqueArray } from '../../utils/arrayUtils';
import { CurrencyCross } from '../currencies';
import {
  ListedInstrument,
  ListedInstrumentLike,
  getInstrumentIdForce,
  isUnknownInstrument,
} from '../instrument/listedInstrument';
import { EStreamTradeDTO } from './eStreamTrade';
import { InstrumentStoreItem } from './instrumentStoreItem';

const SmallThrottleRate = 250;
const LargeThrottleRate = 750;

/**
 * NetBroker-web instrument store to access/update instrument data
 */
export class InstrumentStore {
  /**
   * The store that holds the instrument data
   */
  private store: ObjectSet<InstrumentStoreItem> = new ObjectSet<InstrumentStoreItem>(getInstrumentIdForce);

  /** Callbacks for registerAll callbacks */
  private changeListeners: Record<
    string,
    { instruments: Array<string>; cb: (item: ObjectSet<InstrumentStoreItem>) => void }
  > = {};
  /** IDs of the registerAll listeners. This is the cached keys of the changeListeners map */
  private changeListenerIds: Array<string> = [];

  /** Callbacks for registerTrade callbacks */
  private tradeListeners: Record<string, { micList: Set<string>; cb: (trades: Array<EStreamTradeDTO>) => void }> = {};
  /** IDs of the registerTrade listeners. This is the cached keys of the tradeListeners map */
  private tradeListenerIds: Array<string> = [];

  /** Callbacks for getLater promises */
  private getLaterListeners: Record<
    string,
    { instrument: string; cb: (item: ObjectSet<InstrumentStoreItem>) => void }
  > = {};

  /** Timeout instance used to track when should we call setMany with the collected trades */
  private throttleTimeout: NodeJS.Timer | undefined;
  /** Collected trades to be set when the data is throttled */
  private throttledTrades: Array<EStreamTradeDTO> = [];
  /** Last time setMany was executed */
  private lastSetMany = 0;
  /** Throttle rate in milliseconds, meaning setMany won't be called more than once in this time frame */
  private throttleRate = SmallThrottleRate;

  /**
   * This map contains which instruments are registered ever in the store.
   * This is needed because we need last known price information as well as the instrument's market code, because eStream doesn't return it
   * @private
   */
  private registeredInstruments: Record<string, ListedInstrument> = {};

  /**
   * This map contains which currencies are registered ever in the store.
   * @private
   */
  private registeredCurrencies: Record<CurrencyCross, CurrencyCross> = {};

  constructor(private jotaiStore: ReturnType<typeof createStore>) {}

  /**
   * ⚠️ This function creates a shallow copy of the instrument store items ⚠️
   * Using this will slow down the application like crazy, be careful how you use it
   */
  get instrumentStoreItemsCopy() {
    return this.store.values();
  }

  // WritableAtom<Value, Args, Result> & WithInitialValue<Value>
  private atomCache: Record<string, any> = {};
  private noInstrumentDataAtom = atom<null>(null);

  getInstrumentAtom(
    instrument: null | string | ListedInstrument | ListedInstrumentLike,
  ): WritableAtom<InstrumentStoreItem | null, any, any> {
    if (!instrument) {
      return this.noInstrumentDataAtom;
    }
    const id = this.getId(instrument);
    if (!this.atomCache[id]) {
      this.atomCache[id] = atom(this.get(id));
    }
    return this.atomCache[id];
  }

  /**
   * Registers the given instruments and calls the callback function when the instrument data is changed
   */
  registerAll(
    instrumentList: Array<ListedInstrument | ListedInstrumentLike>,
    cb?: (instrumentStoreItems: ObjectSet<InstrumentStoreItem>) => void,
  ): () => void {
    const deregisterFnId = nanoid();
    if (cb) {
      this.changeListeners[deregisterFnId] = {
        instruments: instrumentList.map(_ => this.getId(_)),
        cb,
      };
      this.changeListenerIds = Object.keys(this.changeListeners);
    }

    // save the instruments in the registeredInstruments map
    instrumentList.forEach(instrument => {
      if ((instrument as unknown as ListedInstrument).mic !== undefined) {
        const instrumentId = this.getId(instrument);
        const nextInstrument = instrument as unknown as ListedInstrument;
        const registeredInstrument = this.registeredInstruments[instrumentId];

        if (registeredInstrument && isUnknownInstrument(nextInstrument) && !isUnknownInstrument(registeredInstrument)) {
          return;
        }

        this.registeredInstruments[instrumentId] = nextInstrument;
      }
    });

    if (cb) {
      return () => {
        delete this.changeListeners[deregisterFnId];
        this.changeListenerIds = Object.keys(this.changeListeners);
      };
    }
    return () => {};
  }

  /**
   * Calls the callback function when a trade is received for the given instrument
   */
  registerTrades(micList: Set<string>, cb: (trades: Array<EStreamTradeDTO>) => void): () => void {
    const deregisterFnId = nanoid();
    this.tradeListeners[deregisterFnId] = {
      micList,
      cb,
    };

    this.tradeListenerIds = Object.keys(this.tradeListeners);
    return () => {
      delete this.tradeListeners[deregisterFnId];
      this.tradeListenerIds = Object.keys(this.tradeListeners);
    };
  }

  get(instrument: string | InstrumentStoreItem): InstrumentStoreItem | undefined {
    const instrumentId = this.getId(instrument);
    return this.store.get(instrumentId);
  }

  private lastSetManyRun = 0;

  // Self note: theoretically, this method should be called only once per instrument, so trades is not an array
  // however we should consider throttling this method to avoid calling it too often, and avoid constant re-rendering
  setMany(
    trades: Array<EStreamTradeDTO> | EStreamTradeDTO,
    hasRealtimeAccess: (market: string) => boolean,
    formatDate: (date: Date) => string,
    nonTradingDays: Record<string, Array<string>>,
  ) {
    const tradesAsArray = Array.isArray(trades) ? trades : [trades];
    // Throttle the setMany method to avoid calling it too often
    const elapsedTimeSinceLastSetMany = Date.now() - this.lastSetMany;
    // If the time elapsed since the last setMany call is less than the throttle rate, we should throttle the call
    if (elapsedTimeSinceLastSetMany < this.throttleRate) {
      if (this.throttleTimeout) {
        // @ts-ignore
        clearTimeout(this.throttleTimeout);
      }
      this.throttledTrades.push(...tradesAsArray);
      // Call setMany after the throttle rate has passed
      this.throttleTimeout = setTimeout(() => {
        this.setMany(this.throttledTrades.splice(0), hasRealtimeAccess, formatDate, nonTradingDays);
      }, this.throttleRate - elapsedTimeSinceLastSetMany);

      // We should return here to avoid executing setMany
      return;
    } else {
      // reset the throttle timeout and the throttled trades
      this.lastSetMany = Date.now();
    }
    // TODO INVESTIGATE IF THIS IS NEEDED
    // give some time to the garbage collector to clean up the throttled trades
    const lastRunBefore = Date.now() - this.lastSetManyRun;
    if (lastRunBefore > SmallThrottleRate) {
      this.throttleRate = LargeThrottleRate;
    } else {
      this.throttleRate = SmallThrottleRate;
    }

    this.lastSetManyRun = Date.now();

    const changeListenerToCall: Array<string> = [];
    const tradeListenerToCall: Array<string> = [];

    if (Array.isArray(trades)) {
      trades.forEach(trade => {
        this.setOne(trade, changeListenerToCall, tradeListenerToCall, hasRealtimeAccess, formatDate, nonTradingDays);
      });
    } else {
      this.setOne(trades, changeListenerToCall, tradeListenerToCall, hasRealtimeAccess, formatDate, nonTradingDays);
    }

    if (changeListenerToCall.length > 0) {
      uniqueArray(changeListenerToCall).forEach(listenerKey => {
        const requiredInstruments = this.store.subset(this.changeListeners[listenerKey].instruments);
        this.changeListeners[listenerKey].cb(requiredInstruments);
      });
    }
    if (tradeListenerToCall.length > 0) {
      uniqueArray(tradeListenerToCall).forEach(listenerKey => {
        this.tradeListeners[listenerKey].cb(
          tradesAsArray.filter(_ => this.tradeListeners[listenerKey].micList.has(_.mic)),
        );
      });
    }
  }

  triggerChangeListeners(instrumentRics: Array<string>) {
    if (instrumentRics.length === 0) {
      return;
    }
    const changeListenerToCall: Array<string> = [];

    this.changeListenerIds.forEach(listenerKey => {
      const instruments = this.changeListeners[listenerKey].instruments;
      if (hasIntersection(instruments, instrumentRics)) {
        changeListenerToCall.push(listenerKey);
      }
    });

    if (changeListenerToCall.length > 0) {
      uniqueArray(changeListenerToCall).forEach(listenerKey => {
        const requiredInstruments = this.store.subset(this.changeListeners[listenerKey].instruments);
        this.changeListeners[listenerKey].cb(requiredInstruments);
      });
    }
  }

  /**
   * Returns true if the store has instrument data for the given RIC
   * @param ric
   */
  has(ric: string) {
    return this.store.hasById(ric);
  }

  /**
   * Clears the store
   */
  cleanup() {
    this.changeListeners = {};
    this.changeListenerIds = [];
    this.tradeListeners = {};
    this.tradeListenerIds = [];
    this.getLaterListeners = {};
    this.throttledTrades = [];
    if (this.throttleTimeout) {
      // @ts-ignore
      clearTimeout(this.throttleTimeout);
    }
    this.store.clear();
  }

  private setOne(
    trade: EStreamTradeDTO,
    changeListenerToCall: Array<string>,
    tradeListenerToCall: Array<string>,
    hasRealtimeAccess: (market: string) => boolean,
    formatDate: (date: Date) => string,
    nonTradingDays: Record<string, Array<string>>,
  ) {
    this.setInstrumentData({
      trade: trade,
      hasRealtimeAccess: hasRealtimeAccess(this.registeredInstruments[trade.ric]?.mic),
      formatDate,
      nonTradingDays,
    });

    this.changeListenerIds.forEach(listenerKey => {
      const instruments = this.changeListeners[listenerKey].instruments;
      if (instruments.includes(getInstrumentIdForce(trade))) {
        changeListenerToCall.push(listenerKey);
      }
    });
    this.tradeListenerIds.forEach(listenerKey => {
      const micList = this.tradeListeners[listenerKey].micList;
      if (micList.has(trade.mic)) {
        tradeListenerToCall.push(listenerKey);
      }
    });
  }

  /**
   * Sets the instrument data in the store and triggers the change listeners
   * Can be used when instrument store data is transferred between browsers
   */
  setInstrumentStoreItem(instrumentStoreItem: Array<InstrumentStoreItem>) {
    this.store.add(instrumentStoreItem);
    this.store.forEach(item => {
      this.jotaiStore.set(this.getInstrumentAtom(item), { ...item });
    });

    // trigger change listeners
    const changeListenerToCall: Array<string> = [];

    this.changeListenerIds.forEach(listenerKey => {
      const instruments = this.changeListeners[listenerKey].instruments;

      instrumentStoreItem.forEach(instrumentStoreItem => {
        if (instruments.includes(getInstrumentIdForce(instrumentStoreItem))) {
          changeListenerToCall.push(listenerKey);
        }
      });
    });

    if (changeListenerToCall.length > 0) {
      uniqueArray(changeListenerToCall).forEach(listenerKey => {
        const requiredInstruments = this.store.subset(this.changeListeners[listenerKey].instruments);
        this.changeListeners[listenerKey].cb(requiredInstruments);
      });
    }
  }

  setInstrumentData({
    trade,
    hasRealtimeAccess,
    formatDate,
    nonTradingDays,
    instrument,
  }: {
    trade: EStreamTradeDTO;
    hasRealtimeAccess: boolean;
    formatDate: (date: Date) => string;
    nonTradingDays: Record<string, Array<string>>;
    instrument?: ListedInstrument;
  }): InstrumentStoreItem {
    this.store.set(
      trade,
      {
        ric: trade.ric,
        mic: trade.mic,
      },
      prevValue =>
        mapEStreamTradeToInstrumentStoreItem(
          trade,
          prevValue,
          instrument || this.registeredInstruments[trade.ric],
          hasRealtimeAccess,
          formatDate,
          nonTradingDays,
          trade.update_type === 'Snapshot',
        ),
    );
    const id = getInstrumentIdForce(trade);
    const updatedValue = this.store.get(trade)!;
    this.jotaiStore.set(this.getInstrumentAtom(id), { ...updatedValue });
    return updatedValue;
  }

  private getId(idOrItem: string | InstrumentStoreItem): string {
    return typeof idOrItem === 'string' ? idOrItem : getInstrumentIdForce(idOrItem);
  }
}
